import { Spinner } from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";

import { useMaterials, useMedia } from "@/hooks";
import type { Material } from "@/types";
import { Button, ControlledDropzone, errorToast, Input, Select } from "@/ui";

interface AddMaterialFormProps {
  onClose: () => void;
}

interface FormValues {
  name: string;
  type: "pdf" | "video";
  pdf?: File;
}

export const AddMaterialForm = ({ onClose }: AddMaterialFormProps) => {
  const { useUpload } = useMedia();
  const { upload, isUploading } = useUpload();
  const queryClient = useQueryClient();

  const { useAddMaterial } = useMaterials();
  const { mutateAsync: addMaterial, isPending } = useAddMaterial();

  const {
    formState: { errors, isValid },
    handleSubmit,
    register,
    control,
    watch,
  } = useForm<FormValues>({
    mode: "all",
    defaultValues: {
      name: "",
      type: "pdf",
    },
  });

  const materialType = watch("type");

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      const materialData: Partial<Material> = {
        name: data.name,
        type: data.type,
        published: false, // Default to false
        url: data.pdf ? `/materials/pdfs/${crypto.randomUUID()}.pdf` : "",
      };

      const newMaterial = await addMaterial(materialData);

      // Upload PDF if provided and wait for completion
      if (data.pdf && newMaterial.id) {
        const result = await upload(
          data.pdf,
          `/materials/${newMaterial.id}/pdf/upload-url`,
        );
        if (!result.success) {
          errorToast("Failed to upload PDF. Please try again.");
          return; // Keep modal open on upload failure
        }
      }

      await queryClient.invalidateQueries({
        queryKey: ["bundles"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["materials"],
      });
      onClose();
    } catch (error) {
      errorToast(error);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex w-full flex-col gap-y-8 sm:w-[540px]"
    >
      <div className="flex flex-col gap-y-8">
        <Input
          id="name"
          label="Material Name"
          placeholder="e.g., MBCT-L Participant Handbook"
          {...register("name", { required: "Material name is required" })}
          error={errors.name?.message}
          required
        />

        <Select
          id="type"
          label="Type"
          options={[
            { value: "pdf", label: "PDF" },
            { value: "video", label: "Video" },
          ]}
          {...register("type", { required: "Type is required" })}
          error={errors.type?.message}
          required
        />

        {materialType === "pdf" && (
          <ControlledDropzone
            name="pdf"
            control={control}
            label="PDF File"
            placeholder="Upload PDF file"
            accept={{ "application/pdf": [".pdf"] }}
          />
        )}
      </div>

      <div className="flex justify-center gap-2">
        <Button onClick={onClose} variant="outlined" className="flex-grow">
          Cancel
        </Button>
        <Button
          type="submit"
          className="flex-grow"
          disabled={!isValid || isPending || isUploading}
        >
          {isPending || isUploading ? (
            <Spinner className="h-5 w-5 animate-spin" />
          ) : (
            "Create Material"
          )}
        </Button>
      </div>
    </form>
  );
};
